import { useTranslation } from 'react-i18next'

interface BadgeCardProps {
  /** Code du badge, qui sert aussi de clé de traduction. */
  readonly code: string
  readonly icon: string
  /** Date d'obtention au format ISO ; absente, le badge est présenté verrouillé. */
  readonly earnedAt?: string
}

export function BadgeCard({ code, icon, earnedAt }: BadgeCardProps) {
  const { t, i18n } = useTranslation()
  const isEarned = earnedAt !== undefined

  const formattedDate = earnedAt
    ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(earnedAt))
    : null

  return (
    <article
      aria-label={t(`gamification.badges.${code}.name`)}
      className={[
        'flex flex-col items-center gap-2 rounded-2xl border p-4 text-center transition-colors',
        isEarned
          ? 'border-secondary/30 bg-secondary/5'
          : 'border-outline-variant bg-surface-low',
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        className={['text-3xl leading-none', isEarned ? '' : 'opacity-30 grayscale'].join(' ')}
      >
        {icon}
      </span>

      <p
        className={[
          'font-display text-sm font-semibold',
          isEarned ? 'text-on-surface' : 'text-outline',
        ].join(' ')}
      >
        {t(`gamification.badges.${code}.name`)}
      </p>

      <p className="text-xs leading-snug text-on-surface-variant">
        {t(`gamification.badges.${code}.description`)}
      </p>

      {formattedDate ? (
        <p className="text-[11px] font-medium text-secondary">
          {t('gamification.earned_on', { date: formattedDate })}
        </p>
      ) : (
        <p className="text-[11px] font-medium text-outline">{t('gamification.locked')}</p>
      )}
    </article>
  )
}
